import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Bitcoin, DollarSign, MessageCircle, Loader2, Smartphone, Wallet } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, errMsg, money, PLATFORMS } from "../lib/api";

const METHODS = [
  ["stripe", CreditCard, "Card", "Visa, Mastercard, Amex via Stripe"],
  ["applepay", Smartphone, "Apple Pay", "Apple Cash to our number"],
  ["paypal", Wallet, "PayPal", "Scan our PayPal QR"],
  ["crypto", Bitcoin, "Crypto", "BTC, ETH or USDT"],
  ["cashapp", DollarSign, "Cash App", "Send to our $cashtag"],
  ["manual", MessageCircle, "Contact me", "We reach out to arrange it"],
];
const CARD = ["stripe"];

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{label}</span>
    <input {...props} className="mt-2 h-12 w-full border border-white/15 bg-black/40 px-4 text-sm text-white placeholder:text-slate-500 focus:border-[#a259ff] focus:outline-none" />
  </label>
);

export const CheckoutDialog = ({ product, onClose, deliveryTime = "" }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [method, setMethod] = useState("stripe");
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [promo, setPromo] = useState(null);
  const open = Boolean(product);

  useEffect(() => {
    setCode("");
    setPromo(null);
  }, [product]);

  const applyCode = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.post("/discounts/check", { code, product_id: product.id });
      setPromo(data ?? null);
      toast.success(`Code ${data?.code} applied`);
    } catch (err) {
      setPromo(null);
      toast.error(errMsg(err));
    }
  };
  const price = promo?.price ?? product?.price;

  useEffect(() => {
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
    return () => {
      window.__lenis?.start();
    };
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { product_id: product.id, email, contact, payment_method: method, origin_url: window.location.origin, discount_code: promo?.code ?? null });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      navigate(`/order/${data?.order?.id}?k=${data?.access_key}`);
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-lenis-prevent className="max-h-[92vh] overflow-y-auto rounded-none border-white/10 bg-[#0b0b11] p-0 text-white sm:max-w-xl" data-testid="account-detail-modal">
        {product && (
          <form onSubmit={submit}>
            <div className="border-b border-white/10 p-6 pr-12">
              <p className="font-mono text-[11px] uppercase tracking-widest text-violet-300">{PLATFORMS[product.platform]?.long} · Rank {product.rank} · {product.money}</p>
              <DialogTitle className="mt-2 font-display text-5xl font-normal uppercase leading-none">{product.name}</DialogTitle>
              <DialogDescription className="mt-2 font-mono text-2xl font-bold text-[#e9d5ff]" data-testid="checkout-price">
                {promo && <span className="mr-3 text-lg text-slate-400 line-through">{money(product.price)}</span>}
                {money(price)}
              </DialogDescription>
              {deliveryTime && <p className="mt-3 font-mono text-xs uppercase tracking-widest text-slate-300" data-testid="checkout-delivery-time">Delivery after payment: <span className="text-white">{deliveryTime}</span></p>}
            </div>
            <div className="space-y-5 p-6">
              <Field label="Email for delivery" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="checkout-email-input" />
              <Field label="Discord / Snapchat / Instagram handle" required minLength={2} value={contact} onChange={(e) => setContact(e.target.value)} placeholder="@yourhandle" data-testid="checkout-contact-input" />
              <div>
                <span className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Promo code</span>
                <div className="mt-2 flex gap-2">
                  <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="OGM10" data-testid="checkout-promo-input" className="h-12 flex-1 border border-white/15 bg-black/40 px-4 font-mono text-sm uppercase text-white placeholder:text-slate-500 focus:border-[#a259ff] focus:outline-none" />
                  <button type="button" onClick={applyCode} data-testid="checkout-promo-apply" className="h-12 border border-white/20 px-5 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:border-[#a259ff] hover:text-[#a259ff]">
                    {promo ? "Applied" : "Apply"}
                  </button>
                </div>
              </div>
              <div>
                <span className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Payment method</span>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  {METHODS.map(([id, Icon, title, sub]) => (
                    <button
                      type="button"
                      key={id}
                      onClick={() => setMethod(id)}
                      data-testid={`checkout-payment-${id}`}
                      className={`border p-4 text-left transition-[border-color,background-color] duration-300 ${method === id ? "border-[#a259ff] bg-[#a259ff]/10" : "border-white/10 hover:border-white/30"}`}
                    >
                      <Icon className={`h-5 w-5 ${method === id ? "text-[#a259ff]" : "text-slate-400"}`} />
                      <p className="mt-3 text-sm font-semibold">{title}</p>
                      <p className="text-xs text-slate-400">{sub}</p>
                    </button>
                  ))}
                </div>
              </div>
              <button
                disabled={busy}
                data-testid="checkout-submit-button"
                className="flex h-14 w-full items-center justify-center gap-2 bg-[#ff3dc8] font-mono text-sm font-bold uppercase tracking-widest text-white transition-shadow duration-300 hover:shadow-[0_0_40px_rgba(255,61,200,0.55)] disabled:opacity-60"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {CARD.includes(method) ? `Pay ${money(price)}` : "Place order"}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
